import { isAccountId } from "../domain/accounts";
import { normalizeExecutor } from "../domain/executors";
import { normalizeBenchmark, normalizeGuide, normalizeScore, normalizeSetting } from "../domain/research";
import { emptyLocalState } from "../domain/sync";
import { isTopicStatus } from "../domain/status";
import { normalizeTask } from "../domain/tasks";
import type { AccountProfile, LocalState, NotionSyncInfo, Topic } from "../types";

function key(userId: string): string {
  return `mediadesk.v1.${userId}`;
}

export function loadLocalState(userId: string): LocalState {
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return emptyLocalState();
    const parsed = JSON.parse(raw) as Partial<LocalState>;
    return {
      topics: Array.isArray(parsed.topics) ? parsed.topics.flatMap((item) => {
        const topic = normalizeTopic(item);
        return topic ? [topic] : [];
      }) : [],
      activities: Array.isArray(parsed.activities) ? parsed.activities : [],
      snapshots: Array.isArray(parsed.snapshots) ? parsed.snapshots : [],
      postMetrics: Array.isArray(parsed.postMetrics) ? parsed.postMetrics : [],
      tasks: Array.isArray(parsed.tasks) ? parsed.tasks.flatMap((item) => {
        const task = normalizeTask(item);
        return task ? [task] : [];
      }) : [],
      accounts: Array.isArray(parsed.accounts) ? parsed.accounts.flatMap((item) => {
        const account = normalizeAccount(item);
        return account ? [account] : [];
      }) : [],
      benchmarks: Array.isArray(parsed.benchmarks) ? parsed.benchmarks.flatMap((item) => {
        const note = normalizeBenchmark(item);
        return note ? [note] : [];
      }) : [],
      styleGuides: Array.isArray(parsed.styleGuides) ? parsed.styleGuides.flatMap((item) => {
        const guide = normalizeGuide(item);
        return guide ? [guide] : [];
      }) : [],
      postScores: Array.isArray(parsed.postScores) ? parsed.postScores.flatMap((item) => {
        const score = normalizeScore(item);
        return score ? [score] : [];
      }) : [],
      scoreSettings: Array.isArray(parsed.scoreSettings) ? parsed.scoreSettings.flatMap((item) => {
        const setting = normalizeSetting(item);
        return setting ? [setting] : [];
      }) : [],
      executors: Array.isArray(parsed.executors) ? parsed.executors.flatMap((item) => {
        const executor = normalizeExecutor(item);
        return executor ? [executor] : [];
      }) : [],
      outbox: Array.isArray(parsed.outbox) ? parsed.outbox : [],
    };
  } catch {
    return emptyLocalState();
  }
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeTopic(value: unknown): Topic | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<Topic>;
  if (typeof raw.id !== "string" || typeof raw.title !== "string") return null;
  return {
    id: raw.id,
    title: raw.title,
    status: isTopicStatus(raw.status) ? raw.status : "待确认",
    batch: raw.batch ?? "",
    angle: raw.angle ?? "",
    audience: raw.audience ?? "",
    sources: raw.sources ?? "",
    createdDate: raw.createdDate ?? null,
    notionUrl: raw.notionUrl ?? "",
    publishUrl: raw.publishUrl ?? null,
    plannedPublishDate: raw.plannedPublishDate ?? null,
    plannedPublishAt: raw.plannedPublishAt ?? null,
    actualPublishAt: raw.actualPublishAt ?? null,
    sourceAssistant: raw.sourceAssistant ?? "",
    reason: raw.reason ?? "",
    heat: finiteNumber(raw.heat),
    heatBasis: raw.heatBasis ?? "",
    referenceUrl: raw.referenceUrl ?? null,
    queueOrder: finiteNumber(raw.queueOrder),
    accountId: isAccountId(raw.accountId) ? raw.accountId : "xiaohongshu",
    recommendRank: finiteNumber(raw.recommendRank),
    viralReason: raw.viralReason ?? "",
    sourceLinks: Array.isArray(raw.sourceLinks) ? raw.sourceLinks.filter((item): item is string => typeof item === "string") : [],
    sourcePlatform: raw.sourcePlatform ?? "",
    sourceTaskId: raw.sourceTaskId ?? null,
    platforms: Array.isArray(raw.platforms) ? raw.platforms.filter((item): item is string => typeof item === "string") : [],
    notes: raw.notes ?? "",
    notesUpdatedAt: raw.notesUpdatedAt ?? null,
    lastEditedTime: raw.lastEditedTime ?? raw.updatedAt ?? "",
    updatedAt: raw.updatedAt ?? raw.lastEditedTime ?? "",
    inTrash: Boolean(raw.inTrash),
    syncError: raw.syncError ?? null,
  };
}

function normalizeAccount(value: unknown): AccountProfile | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<AccountProfile>;
  if (!isAccountId(raw.id) || typeof raw.displayName !== "string") return null;
  return {
    id: raw.id,
    platform: typeof raw.platform === "string" && raw.platform.trim() ? raw.platform.trim() : raw.id === "douyin" ? "抖音" : "小红书",
    displayName: raw.displayName.slice(0, 80),
    profileUrl: typeof raw.profileUrl === "string" ? raw.profileUrl : "",
    note: typeof raw.note === "string" ? raw.note : "",
    addedAt: typeof raw.addedAt === "string" ? raw.addedAt : null,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : "",
  };
}

export function saveLocalState(userId: string, state: LocalState): void {
  localStorage.setItem(key(userId), JSON.stringify(state));
}

export function loadNotionInfo(userId: string): NotionSyncInfo {
  try {
    const raw = localStorage.getItem(`${key(userId)}.notion`);
    if (!raw) return { lastSyncAt: null, lastError: null, schema: null };
    return JSON.parse(raw) as NotionSyncInfo;
  } catch {
    return { lastSyncAt: null, lastError: null, schema: null };
  }
}

export function saveNotionInfo(userId: string, info: NotionSyncInfo): void {
  localStorage.setItem(`${key(userId)}.notion`, JSON.stringify(info));
}
